import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge } from "@/components/Status";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { HardDrive, Plus, Trash2, PlugZap, RefreshCw, Code2, Copy, Download, Pencil, ArrowUpCircle, PhoneCall } from "lucide-react";

export default function Servers() {
  const [servers, setServers] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deploy, setDeploy] = useState(null); // {name, filename, script}
  const [editServer, setEditServer] = useState(null); // server being edited
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [agentVersions, setAgentVersions] = useState({}); // {[server_id]: {local, remote, up_to_date}}
  const [updatingAgent, setUpdatingAgent] = useState(null);
  const [contactsServer, setContactsServer] = useState(null); // {id, name}
  const [contacts, setContacts] = useState({ data: [], loading: false, error: null });
  // Assistant d'ajout
  const [step, setStep] = useState(1);
  const [wizard, setWizard] = useState({ name: "", ip: "", region: "EU-West" });
  const [prereqs, setPrereqs] = useState({ os: false, specs: false, ip: false, ports: false });
  const [created, setCreated] = useState(null); // {name, filename, script, ami_secret, agent_token, agent_url}

  const load = () => api.get("/servers").then((r) => setServers(r.data));

  const loadAgentVersions = (serverList) => {
    serverList.forEach((s) => {
      api.get(`/servers/${s.id}/agent-version`).then(({ data }) => {
        setAgentVersions((prev) => ({ ...prev, [s.id]: data }));
      }).catch(() => {});
    });
  };

  useEffect(() => {
    api.get("/servers").then((r) => {
      setServers(r.data);
      loadAgentVersions(r.data);
    });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/servers", {
        name: wizard.name,
        ip: wizard.ip,
        region: wizard.region,
      });
      const sc = await api.get(`/servers/${data.id}/deploy-script`);
      setCreated({
        name: data.name,
        filename: sc.data.filename,
        script: sc.data.script,
        ami_secret: data.ami_secret,
        agent_token: data.agent_token,
        agent_url: data.agent_url,
        guest_ws_url: sc.data.guest_ws_url,
        caddy_snippet: sc.data.caddy_snippet,
      });
      setStep(3);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openWizard = (o) => {
    setOpen(o);
    if (o) {
      setStep(1);
      setWizard({ name: "", ip: "", region: "EU-West" });
      setPrereqs({ os: false, specs: false, ip: false, ports: false });
      setCreated(null);
    }
  };

  const downloadText = (text, filename, type = "text/plain") => {
    const blob = new Blob([text], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/servers/${id}`);
      toast.success("Serveur supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openEdit = (s) => {
    setEditForm({
      name: s.name || "",
      ip: s.ip || "",
      host: s.host || "",
      wg_ip: s.wg_ip || "",
      sip_port: s.sip_port ?? 6060,
      capacity: s.capacity ?? 500,
      region: s.region || "EU-West",
      ami_host: s.ami_host || "",
      ami_port: s.ami_port ?? 5938,
      ami_user: s.ami_user || "",
      ami_secret: s.ami_secret || "",
      agent_url: s.agent_url || "",
      agent_token: s.agent_token || "",
    });
    setEditServer(s);
  };

  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.put(`/servers/${editServer.id}`, editForm);
      toast.success("Serveur mis à jour");
      setEditServer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const updateAgent = async (id) => {
    setUpdatingAgent(id);
    const t = toast.loading("Envoi de la mise à jour agent…");
    try {
      const { data } = await api.post(`/servers/${id}/update-agent`);
      toast.dismiss(t);
      toast.success(`Agent mis à jour vers v${data.version} — redémarrage en cours`);
      setTimeout(() => {
        api.get(`/servers/${id}/agent-version`).then(({ data: v }) =>
          setAgentVersions((prev) => ({ ...prev, [id]: v }))
        ).catch(() => {});
      }, 3000);
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setUpdatingAgent(null);
    }
  };

  const test = async (id) => {
    const t = toast.loading("Test AMI en cours…");
    try {
      const { data } = await api.post(`/servers/${id}/test`);
      toast.dismiss(t);
      data.ok ? toast.success(data.message) : toast.error(data.error);
      load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const sync = async (id) => {
    const t = toast.loading("Synchronisation du statut SIP…");
    try {
      const { data } = await api.post(`/servers/${id}/sync`);
      toast.dismiss(t);
      data.ok
        ? toast.success(`${data.rooms_updated} chambre(s) mises à jour (${data.contacts} contacts)`)
        : toast.error(data.error);
      load();
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const showDeploy = async (server) => {
    const t = toast.loading("Génération du script de déploiement…");
    try {
      const { data } = await api.get(`/servers/${server.id}/deploy-script`);
      toast.dismiss(t);
      setDeploy({ name: server.name, filename: data.filename, script: data.script, guest_ws_url: data.guest_ws_url, caddy_snippet: data.caddy_snippet });
    } catch (e) {
      toast.dismiss(t);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const downloadDeploy = () => {
    downloadText(deploy.script, deploy.filename, "text/x-shellscript");
  };

  const openContacts = async (s) => {
    setContactsServer({ id: s.id, name: s.name });
    setContacts({ data: [], loading: true, error: null });
    try {
      const { data } = await api.get(`/servers/${s.id}/pjsip-contacts`);
      setContacts({ data: data.contacts || [], loading: false, error: data.error || null });
    } catch (e) {
      setContacts({ data: [], loading: false, error: formatApiError(e.response?.data?.detail) });
    }
  };

  const allPrereqs = prereqs.os && prereqs.specs && prereqs.ip && prereqs.ports;
  const PREREQ_ITEMS = [
    ["os", "VPS Debian 13 (trixie) fraîchement installé, accès root SSH"],
    ["specs", "2 vCPU / 2 Go RAM minimum, ~5 Go de disque"],
    ["ip", "IP publique fixe attribuée au serveur"],
    ["ports", "Ports ouvrables : SIP 6060, RTP 40000-40100, AMI 5938, agent 9443"],
  ];

  return (
    <div data-testid="servers-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            Infrastructure
          </p>
          <h1 className="mt-1 font-head text-3xl font-black tracking-tighter text-zinc-900">
            Serveurs SIP
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Registre des serveurs Asterisk (multi-serveur). Chaque hôtel est rattaché à un serveur.
          </p>
        </div>
        <Dialog open={open} onOpenChange={openWizard}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-server-btn"
              className="rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="mr-1 h-4 w-4" /> Ajouter un serveur
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl rounded-sm">
            <DialogHeader>
              <DialogTitle className="font-head tracking-tight">
                Ajouter un serveur SIP — étape {step}/3
              </DialogTitle>
            </DialogHeader>

            {step === 1 && (
              <div className="space-y-3 py-2">
                <p className="text-sm text-zinc-500">
                  On commence par l'essentiel : le nom et l'IP publique du serveur. Les secrets
                  AMI et le jeton de l'agent sont générés automatiquement.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Nom" testid="server-name" value={wizard.name} onChange={(v) => setWizard({ ...wizard, name: v })} placeholder="asterisk-paris-01" full />
                  <Field label="IP publique" testid="server-ip" value={wizard.ip} onChange={(v) => setWizard({ ...wizard, ip: v })} placeholder="51.210.44.10" />
                  <Field label="Région" testid="server-region" value={wizard.region} onChange={(v) => setWizard({ ...wizard, region: v })} />
                </div>
                <DialogFooter>
                  <Button
                    data-testid="wizard-next-btn"
                    onClick={() => setStep(2)}
                    disabled={!wizard.name || !wizard.ip}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    Continuer
                  </Button>
                </DialogFooter>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-3 py-2">
                <p className="text-sm text-zinc-500">
                  Vérifions ensemble les prérequis avant de générer le script. Cochez chaque point
                  une fois validé sur votre serveur.
                </p>
                <div className="space-y-2">
                  {PREREQ_ITEMS.map(([k, label]) => (
                    <label
                      key={k}
                      className="flex cursor-pointer items-start gap-2 rounded-sm border border-zinc-200 bg-white px-3 py-2 text-sm text-zinc-700"
                    >
                      <input
                        data-testid={`prereq-${k}`}
                        type="checkbox"
                        checked={prereqs[k]}
                        onChange={(e) => setPrereqs({ ...prereqs, [k]: e.target.checked })}
                        className="mt-0.5 h-4 w-4 rounded-sm"
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <DialogFooter className="gap-2">
                  <Button variant="outline" onClick={() => setStep(1)} className="rounded-sm">
                    Retour
                  </Button>
                  <Button
                    data-testid="wizard-generate-btn"
                    onClick={save}
                    disabled={!allPrereqs || saving}
                    className="rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? "Génération…" : "Générer le script"}
                  </Button>
                </DialogFooter>
              </div>
            )}

            {step === 3 && created && (
              <div className="space-y-3 py-2">
                <div className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  Serveur <strong>{created.name}</strong> créé. Conservez ces identifiants — ils
                  sont déjà intégrés au script ci-dessous.
                </div>
                <div className="grid grid-cols-2 gap-2 font-mono text-xs">
                  <div className="rounded-sm border border-zinc-200 bg-white p-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">AMI secret</div>
                    <div data-testid="created-ami-secret" className="break-all text-zinc-800">{created.ami_secret}</div>
                  </div>
                  <div className="rounded-sm border border-zinc-200 bg-white p-2">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-400">Agent token</div>
                    <div data-testid="created-agent-token" className="break-all text-zinc-800">{created.agent_token}</div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="wizard-copy-script"
                    onClick={() => {
                      navigator.clipboard.writeText(created.script);
                      toast.success("Script copié");
                    }}
                    className="h-8 rounded-sm bg-blue-600 hover:bg-blue-700"
                  >
                    <Copy className="mr-1 h-3.5 w-3.5" /> Copier le script
                  </Button>
                  <Button
                    data-testid="wizard-download-script"
                    onClick={() => downloadText(created.script, created.filename, "text/x-shellscript")}
                    variant="outline"
                    className="h-8 rounded-sm"
                  >
                    <Download className="mr-1 h-3.5 w-3.5" /> Télécharger
                  </Button>
                </div>
                <pre
                  data-testid="wizard-script-preview"
                  className="max-h-[340px] overflow-auto rounded-sm bg-zinc-950 p-4 font-mono text-[11px] leading-relaxed text-zinc-300"
                >
                  {created.script}
                </pre>
                {created.caddy_snippet && (
                  <div data-testid="wizard-webrtc" className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs">
                    <div className="mb-1 font-semibold text-amber-900">Contact QR (WebRTC) — étape plateforme</div>
                    <p className="mb-2 text-amber-800">
                      Le script installe coturn + Asterisk /ws. Pour router le WSS des invités, ajoutez ce bloc
                      dans le snippet <code>(site)</code> du Caddyfile de la plateforme (avant le <code>handle</code> final),
                      puis <code>systemctl reload caddy</code>. URL WSS de ce serveur : <code className="break-all">{created.guest_ws_url}</code>
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={() => { navigator.clipboard.writeText(created.caddy_snippet); toast.success("Snippet Caddy copié"); }}
                        className="h-7 rounded-sm bg-amber-600 hover:bg-amber-700"
                      >
                        <Copy className="mr-1 h-3.5 w-3.5" /> Copier le snippet Caddy
                      </Button>
                    </div>
                    <pre className="mt-2 max-h-40 overflow-auto rounded-sm bg-zinc-950 p-3 font-mono text-[11px] text-zinc-300">{created.caddy_snippet}</pre>
                  </div>
                )}
                <DialogFooter>
                  <Button data-testid="wizard-finish-btn" onClick={() => setOpen(false)} className="rounded-sm bg-zinc-900 hover:bg-zinc-800">
                    Terminer
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {servers.map((s) => (
          <div
            key={s.id}
            data-testid={`server-card-${s.id}`}
            className="rounded-sm border border-zinc-200 bg-white p-5"
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <HardDrive className="h-4 w-4 text-zinc-400" />
                <span className="font-head font-bold tracking-tight text-zinc-900">{s.name}</span>
              </div>
              <StatusBadge status={s.status} />
            </div>
            <dl className="mt-4 space-y-1.5 font-mono text-xs">
              <Row k="IP" v={s.ip} />
              <Row k="PORT" v={s.sip_port} />
              <Row k="RÉGION" v={s.region} />
              <Row k="MOTEUR" v={s.engine} />
              <Row k="HÔTELS" v={`${s.hotels_count} hôtels`} />
              {agentVersions[s.id] && (
                <Row
                  k="AGENT"
                  v={
                    <span className={agentVersions[s.id].up_to_date ? "text-emerald-600" : "text-amber-600"}>
                      {agentVersions[s.id].remote_version}
                      {!agentVersions[s.id].up_to_date && agentVersions[s.id].remote_version !== "unreachable" && ` → ${agentVersions[s.id].local_version}`}
                    </span>
                  }
                />
              )}
            </dl>
            {(() => {
              const cap = s.capacity || 500;
              const total = s.rooms_count ?? 0;
              const online = s.rooms_online ?? 0;
              const pctTotal  = Math.min(100, Math.round((total  / cap) * 100));
              const pctOnline = Math.min(100, Math.round((online / cap) * 100));
              const color = pctOnline < 80 ? "bg-emerald-500" : pctOnline < 95 ? "bg-amber-400" : "bg-red-500";
              return (
                <div className="mt-4 space-y-1.5">
                  <div className="flex items-center justify-between font-mono text-[9px] text-zinc-400">
                    <span><span className="text-zinc-600 font-semibold">{online}</span> online · {total} total · {cap} max</span>
                    <span className={pctOnline < 80 ? "text-emerald-600" : pctOnline < 95 ? "text-amber-500" : "text-red-500"}>{pctOnline}%</span>
                  </div>
                  <div className="relative h-[4px] w-full rounded-full bg-zinc-100">
                    {/* Total rooms fill (grey) */}
                    <div className="absolute inset-y-0 left-0 rounded-full bg-zinc-300 transition-all" style={{ width: `${pctTotal}%` }} />
                    {/* Online rooms fill (colored) */}
                    <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-700 ${color}`} style={{ width: `${pctOnline}%` }} />
                  </div>
                </div>
              );
            })()}
            <div className="mt-3 flex items-center gap-0.5 border-t border-zinc-100 pt-2">
              <button
                data-testid={`test-server-${s.id}`}
                title="Tester"
                onClick={() => test(s.id)}
                className="p-1.5 rounded-sm text-zinc-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
              >
                <PlugZap className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`sync-server-${s.id}`}
                title="Synchroniser"
                onClick={() => sync(s.id)}
                className="p-1.5 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`deploy-server-${s.id}`}
                title="Déploiement"
                onClick={() => showDeploy(s)}
                className="p-1.5 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              >
                <Code2 className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`edit-server-${s.id}`}
                title="Modifier"
                onClick={() => openEdit(s)}
                className="p-1.5 rounded-sm text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <button
                data-testid={`contacts-server-${s.id}`}
                title="Contacts PJSIP"
                onClick={() => openContacts(s)}
                className="p-1.5 rounded-sm text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
              >
                <PhoneCall className="h-3.5 w-3.5" />
              </button>
              {agentVersions[s.id] && !agentVersions[s.id].up_to_date && agentVersions[s.id].remote_version !== "unreachable" && (
                <button
                  data-testid={`update-agent-${s.id}`}
                  title="MàJ agent"
                  onClick={() => updateAgent(s.id)}
                  disabled={updatingAgent === s.id}
                  className="p-1.5 rounded-sm text-amber-500 hover:text-amber-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
                >
                  <ArrowUpCircle className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                data-testid={`delete-server-${s.id}`}
                title="Supprimer"
                onClick={() => remove(s.id)}
                className="ml-auto p-1.5 rounded-sm text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editServer} onOpenChange={(o) => !o && setEditServer(null)}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">
              Modifier le serveur — {editServer?.name}
            </DialogTitle>
          </DialogHeader>
          {editServer && (
            <div className="space-y-4 py-2">
              <Section label="Identité">
                <EF label="Nom" value={editForm.name} onChange={(v) => setEditForm((f) => ({ ...f, name: v }))} />
                <EF label="Région" value={editForm.region} onChange={(v) => setEditForm((f) => ({ ...f, region: v }))} />
              </Section>
              <Section label="Réseau">
                <EF label="IP publique" value={editForm.ip} onChange={(v) => setEditForm((f) => ({ ...f, ip: v }))} placeholder="51.210.0.1" />
                <EF label="Host SIP (FQDN)" value={editForm.host} onChange={(v) => setEditForm((f) => ({ ...f, host: v }))} placeholder="sip.example.com" />
                <EF label="IP WireGuard" value={editForm.wg_ip} onChange={(v) => setEditForm((f) => ({ ...f, wg_ip: v }))} placeholder="10.99.0.1" />
                <EF label="Port SIP" value={editForm.sip_port} type="number" onChange={(v) => setEditForm((f) => ({ ...f, sip_port: Number(v) }))} />
                <EF label="Capacité" value={editForm.capacity} type="number" onChange={(v) => setEditForm((f) => ({ ...f, capacity: Number(v) }))} />
              </Section>
              <Section label="AMI">
                <EF label="Host AMI" value={editForm.ami_host} onChange={(v) => setEditForm((f) => ({ ...f, ami_host: v }))} />
                <EF label="Port AMI" value={editForm.ami_port} type="number" onChange={(v) => setEditForm((f) => ({ ...f, ami_port: Number(v) }))} />
                <EF label="Utilisateur AMI" value={editForm.ami_user} onChange={(v) => setEditForm((f) => ({ ...f, ami_user: v }))} />
                <EF label="Secret AMI" value={editForm.ami_secret} onChange={(v) => setEditForm((f) => ({ ...f, ami_secret: v }))} mono />
              </Section>
              <Section label="Agent HTTP">
                <EF label="URL agent" value={editForm.agent_url} onChange={(v) => setEditForm((f) => ({ ...f, agent_url: v }))} placeholder="https://10.99.0.1:9443" full />
                <EF label="Token agent" value={editForm.agent_token} onChange={(v) => setEditForm((f) => ({ ...f, agent_token: v }))} mono full />
              </Section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditServer(null)} className="rounded-sm">Annuler</Button>
            <Button onClick={saveEdit} disabled={editSaving || !editForm.name || !editForm.ip} className="rounded-sm bg-blue-600 hover:bg-blue-700">
              {editSaving ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!contactsServer} onOpenChange={(o) => !o && setContactsServer(null)}>
        <DialogContent className="max-w-4xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">
              Contacts PJSIP — {contactsServer?.name}
            </DialogTitle>
          </DialogHeader>
          {contacts.loading && (
            <p className="py-8 text-center text-sm text-zinc-400">Interrogation AMI…</p>
          )}
          {contacts.error && !contacts.loading && (
            <p className="py-4 text-center text-sm text-red-500">{contacts.error}</p>
          )}
          {!contacts.loading && !contacts.error && contacts.data.length === 0 && (
            <p className="py-8 text-center text-sm text-zinc-400">Aucun contact enregistré</p>
          )}
          {!contacts.loading && contacts.data.length > 0 && (
            <div className="overflow-auto max-h-[520px]">
              <table className="w-full text-xs font-mono">
                <thead className="sticky top-0 bg-zinc-50">
                  <tr className="border-b border-zinc-200">
                    <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400">AOR</th>
                    <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400">IP</th>
                    <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400">Port</th>
                    <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400">Statut</th>
                    <th className="px-3 py-2 text-right text-[10px] font-bold uppercase tracking-wider text-zinc-400">RTT</th>
                    <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-400">User-Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.data.map((c, i) => {
                    const avail = c.status.toLowerCase().includes("avail");
                    const unavail = c.status.toLowerCase().includes("unavail") || c.status.toLowerCase().includes("unreachable");
                    return (
                      <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                        <td className="px-3 py-1.5 font-semibold text-zinc-800">{c.aor}</td>
                        <td className="px-3 py-1.5 text-zinc-600">{c.ip || "—"}</td>
                        <td className="px-3 py-1.5 text-zinc-500">{c.port}</td>
                        <td className="px-3 py-1.5">
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                            avail ? "bg-emerald-50 text-emerald-700" :
                            unavail ? "bg-red-50 text-red-600" :
                            "bg-zinc-100 text-zinc-500"
                          }`}>
                            {c.status || "?"}
                          </span>
                        </td>
                        <td className="px-3 py-1.5 text-right text-zinc-500">
                          {c.rtt_ms != null ? `${c.rtt_ms} ms` : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-zinc-400 max-w-[200px] truncate">{c.user_agent || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <p className="mt-2 px-3 pb-2 text-[10px] text-zinc-400">
                {contacts.data.length} contact{contacts.data.length > 1 ? "s" : ""} enregistré{contacts.data.length > 1 ? "s" : ""}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!deploy} onOpenChange={(o) => !o && setDeploy(null)}>
        <DialogContent className="max-w-3xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-head tracking-tight">
              Script de déploiement Debian 13 — {deploy?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500">
            Copiez-collez ce script sur un VPS Debian 13 neuf (en root). Il installe Asterisk,
            l'AMI restreint, l'agent HTTPS (identifiants déjà pré-remplis) et le firewall.
            Renseignez <code className="font-mono">HOTELSIP_IP</code> en tête pour l'allowlist.
          </p>
          <div className="flex gap-2">
            <Button
              data-testid="copy-deploy-script"
              onClick={() => {
                navigator.clipboard.writeText(deploy.script);
                toast.success("Script copié");
              }}
              className="h-8 rounded-sm bg-blue-600 hover:bg-blue-700"
            >
              <Copy className="mr-1 h-3.5 w-3.5" /> Copier
            </Button>
            <Button
              data-testid="download-deploy-script"
              onClick={downloadDeploy}
              variant="outline"
              className="h-8 rounded-sm"
            >
              <Download className="mr-1 h-3.5 w-3.5" /> Télécharger
            </Button>
          </div>
          <pre
            data-testid="deploy-script-preview"
            className="max-h-[420px] overflow-auto rounded-sm bg-zinc-950 p-4 font-mono text-[11px] leading-relaxed text-zinc-300"
          >
            {deploy?.script}
          </pre>
          {deploy?.caddy_snippet && (
            <div data-testid="deploy-webrtc" className="mt-3 rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs">
              <div className="mb-1 font-semibold text-amber-900">Contact QR (WebRTC) — snippet Caddy plateforme</div>
              <p className="mb-2 text-amber-800">
                À ajouter dans le snippet <code>(site)</code> du Caddyfile de la plateforme puis
                <code> systemctl reload caddy</code>. WSS de ce serveur : <code className="break-all">{deploy.guest_ws_url}</code>
              </p>
              <Button
                onClick={() => { navigator.clipboard.writeText(deploy.caddy_snippet); toast.success("Snippet Caddy copié"); }}
                className="h-7 rounded-sm bg-amber-600 hover:bg-amber-700"
              >
                <Copy className="mr-1 h-3.5 w-3.5" /> Copier le snippet Caddy
              </Button>
              <pre className="mt-2 max-h-40 overflow-auto rounded-sm bg-zinc-950 p-3 font-mono text-[11px] text-zinc-300">{deploy.caddy_snippet}</pre>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-zinc-100 pb-1 last:border-0">
      <span className="text-zinc-400">{k}</span>
      <span className="text-zinc-800">{v}</span>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, full, testid }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">
        {label}
      </Label>
      <Input
        data-testid={`${testid}-input`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 rounded-sm font-mono text-sm"
      />
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function EF({ label, value, onChange, placeholder, type = "text", mono, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{label}</Label>
      <Input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 rounded-sm text-sm ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
